import React, {useMemo} from 'react';

import {useStore} from 'effector-react';

import {TFunction} from 'i18next';

import {useTranslation} from 'react-i18next';

import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {PRODUCT_STATUS_COLORS} from 'core/constants/statuses';
import {ProductsPackageType, ProductStatus} from 'core/dictionaries';
import {
    $state,
    navigateBack,
    navigateToChildren,
    resetNavigation,
} from 'models/documents/create/codesTree';

import {DocumentsCodesTreeRow} from 'types';
import {PackageTypes} from 'types/gtins';
import {ValidatedProductsGridAccessors} from 'types/validation';

import {handleNil} from 'utils/handleNil';

import {NestedCode} from '../../common/styled';

const convertNestedData = (
    data: DocumentsCodesTreeRow[] | undefined,
    t: TFunction,
    hasChildren?: (type?: PackageTypes) => boolean | undefined,
): Partial<DocumentsCodesTreeRow>[] => {
    if (!data) return [];

    return data.map((elem) => ({
        [ValidatedProductsGridAccessors.name]: handleNil(elem.productName),
        [ValidatedProductsGridAccessors.code]: handleNil(elem.identityCode, (value) => {
            if (!hasChildren?.(elem.packagingType)) return value;
            return (
                <NestedCode
                    onClick={() =>
                        navigateToChildren({
                            identityCode: elem.identityCode,
                            gtin: elem.gtin,
                            packagingType: elem.packagingType,
                            productName: elem.productName,
                            status: elem.status,
                        })
                    }
                >
                    {value}
                </NestedCode>
            );
        }),
        [ValidatedProductsGridAccessors.status]: handleNil(elem.status, (value: ProductStatus) => (
            <StatusWrapper color={PRODUCT_STATUS_COLORS[value ?? 'DEFAULT']}>
                {t(`dictionaries.products.status.${value}`)}
            </StatusWrapper>
        )),
        [ValidatedProductsGridAccessors.packageType]: handleNil(
            elem.packagingType,
            (value: ProductsPackageType) => t(`dictionaries.products.packageType.${value}`),
        ),
    }));
};

export const useProductCodesNavigation = () => {
    const state = useStore($state);
    const {t} = useTranslation();

    const NESTED_PACKAGE_TYPES: PackageTypes[] = [
        PackageTypes.BOX,
        PackageTypes.PALLET,
        PackageTypes.CUSTOMS_UNIT,
        PackageTypes.CONTAINER,
        PackageTypes.GROUP,
    ];
    const isNavigable = (type?: PackageTypes) => type && NESTED_PACKAGE_TYPES.includes(type);

    const mappedData = useMemo(() => convertNestedData(state.currentData, t, isNavigable), [
        state.currentData,
        t,
    ]);

    return {
        state,
        mappedData,
        toChild: navigateToChildren,
        toBack: navigateBack,
        reset: resetNavigation,
        isNavigable,
    };
};
